import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchAllIngredients } from "@/redux/slice/essential-menu-slice/Ingredients-slice";
import { fetchAllAddOns } from "@/redux/slice/essential-menu-slice/add-ons-slice";
import { fetchAllCategories } from "@/redux/slice/essential-menu-slice/category-slice";
import { fetchAllAllergens } from "@/redux/slice/essential-menu-slice/allergen-slice";
import { fetchAllBases } from "@/redux/slice/essential-menu-slice/base-slice";
import { fetchAllBrands } from "@/redux/slice/essential-menu-slice/brand-slice";
import { fetchAllCrusts } from "@/redux/slice/essential-menu-slice/crusts-slice";
import { fetchAllSalads } from "@/redux/slice/essential-menu-slice/salads-slice";
import { fetchAllSauces } from "@/redux/slice/essential-menu-slice/sauces-slice";
import { fetchAllToppings } from "@/redux/slice/essential-menu-slice/toppings-slice";
import { fetchAllVariants } from "@/redux/slice/essential-menu-slice/variants-slice";
import { fetchAllModifiers } from "@/redux/slice/essential-menu-slice/modifiers-slice";

export const useFetchAllMenuData = (
  restaurant_id,
  menu_id,
  options = { fetchOnly: null }
) => {
  const dispatch = useDispatch();

  const states = {
    ingredients: useSelector((s) => s.essentialMenu.ingredients),
    categories: useSelector((s) => s.essentialMenu.categories),
    addOns: useSelector((s) => s.essentialMenu.addOns),
    allergens: useSelector((s) => s.essentialMenu.allergens),
    bases: useSelector((s) => s.essentialMenu.bases),
    brands: useSelector((s) => s.essentialMenu.brands),
    crusts: useSelector((s) => s.essentialMenu.crusts),
    salads: useSelector((s) => s.essentialMenu.salads),
    sauces: useSelector((s) => s.essentialMenu.sauces),
    toppings: useSelector((s) => s.essentialMenu.toppings),
    variants: useSelector((s) => s.essentialMenu.variants),
    modifiers: useSelector((s) => s.essentialMenu.modifiers),
  };

  const fetchOnlyKeys = useMemo(() => {
    if (!options.fetchOnly) return null;
    return Array.isArray(options.fetchOnly)
      ? options.fetchOnly
      : [options.fetchOnly];
  }, [options.fetchOnly]);

  const shouldFetch = (key) => {
    if (!fetchOnlyKeys) return true;
    return fetchOnlyKeys.includes(key);
  };

  const fetchAll = () => {
    if (!restaurant_id || !menu_id) return;

    if (shouldFetch("ingredients"))
      dispatch(fetchAllIngredients({ restaurant_id, menu_id }));
    if (shouldFetch("categories"))
      dispatch(fetchAllCategories({ restaurant_id, menu_id }));
    if (shouldFetch("addOns"))
      dispatch(fetchAllAddOns({ restaurant_id, menu_id }));
    if (shouldFetch("allergens"))
      dispatch(fetchAllAllergens({ restaurant_id, menu_id }));
    if (shouldFetch("bases"))
      dispatch(fetchAllBases({ restaurant_id, menu_id }));
    if (shouldFetch("brands"))
      dispatch(fetchAllBrands({ restaurant_id, menu_id }));
    if (shouldFetch("crusts"))
      dispatch(fetchAllCrusts({ restaurant_id, menu_id }));
    if (shouldFetch("salads"))
      dispatch(fetchAllSalads({ restaurant_id, menu_id }));
    if (shouldFetch("sauces"))
      dispatch(fetchAllSauces({ restaurant_id, menu_id }));
    if (shouldFetch("toppings"))
      dispatch(fetchAllToppings({ restaurant_id, menu_id }));
    if (shouldFetch("variants"))
      dispatch(fetchAllVariants({ restaurant_id, menu_id }));
    if (shouldFetch("modifiers"))
      dispatch(fetchAllModifiers({ restaurant_id, menu_id }));
  };

  useEffect(() => {
    fetchAll();
  }, [restaurant_id, menu_id, fetchOnlyKeys]);

  const groupLoading = Object.entries(states)
    .filter(([key]) => shouldFetch(key))
    .some(([_, state]) => state.getLoading);

  // const dataWithErrors = Object.entries(states).reduce((acc, [key, state]) => {
  //   acc[key] = shouldFetch(key) ? state[dataKeys[key]] || [] : [];
  //   acc[`${key}Error`] = shouldFetch(key) ? state.error || null : null;
  //   return acc;
  // }, {});

  return {
    ...Object.fromEntries(
      Object.entries(states).map(([key, state]) => [
        key,
        shouldFetch(key) ? state[key] || [] : [],
      ])
    ),
    groupLoading,
    refresh: fetchAll,
  };
};
